import dayjs, { Dayjs } from 'dayjs';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from './enums';

import { PaymentPeriod, Period, PeriodLicense } from 'src/types/tax';
import { C_TOT_PAYMENT_STATUS_INCOMPLETE } from './consts';

// Calculates the start and end date for a quarter based period
const getQuarterStartEndDates = (
  year: number,
  quarter: number,
  shift: number = 0
): { startDate: Dayjs; endDate: Dayjs } => {
  const normalizedShift = shift % 3;
  let startMonth = (quarter - 1) * 3 + normalizedShift;
  if (startMonth < 0) {
    startMonth = 12 + startMonth;
  }
  const startDate = dayjs(new Date(year, startMonth, 1));
  const endDate = startDate.add(2, 'month').endOf('month').startOf('day');
  return {
    startDate: startDate,
    endDate: endDate,
  };
};

const datesMatch = (dateOne?: Date | string, dateTwo?: string): boolean => {
  if (!dateOne || !dateTwo) return false;
  return dayjs(dateOne).isSame(dayjs(dateTwo, 'day'));
};

const calculateSelectedOnLoad = (
  period: Period | undefined,
  existingData: PaymentPeriod | undefined,
  startDate: string,
  endDate: string
): boolean => {
  if (period?.startDate && period?.endDate) {
    const startDateMatchPreSelected = datesMatch(period.startDate, startDate);
    const endDateMatchPreSelected = datesMatch(period.endDate, endDate);
    if (startDateMatchPreSelected && endDateMatchPreSelected) {
      return true;
    }
  } else if (existingData) {
    return true;
  }
  return false;
};

const addsLicensesSelectedProperty = (
  licenses: Array<PeriodLicense>,
  selectedPeriod: PaymentPeriod | undefined,
  selectedLicenses: Array<PaymentPeriod>
) => {
  // Add selected property to all licenses if matching period found, otherwise use empty array
  return licenses.map((license: PeriodLicense) => {
    // Check for matching license in existingData
    const existingLicense = selectedPeriod?.licenses.find(
      (existing) => existing.license === license.license
    );

    // Use existing license data if found, otherwise use the matchingPeriod license data
    const licenseData = existingLicense || license;

    const licenseSelected =
      !!existingLicense ||
      (!selectedLicenses.length &&
        licenseData.totPaidStatus === C_TOT_PAYMENT_STATUS_INCOMPLETE);

    // Set the selected property
    return {
      ...licenseData,
      selected: licenseSelected,
    };
  });
};

function matchPeriod(
  paymentPeriods: Array<PaymentPeriod>,
  startDate: string,
  endDate: string
): PaymentPeriod | undefined {
  return paymentPeriods.find(
    (period) =>
      datesMatch(period.startDate, startDate) &&
      datesMatch(period.endDate, endDate)
  );
}

export function getFirstDateOfQuarter(date: Dayjs) {
  const zeroIndexPeriod = Math.floor(date.month() / 3) * 3;

  const firstDateOfPeriod = new Date(date.year(), zeroIndexPeriod, 1);
  return dayjs(firstDateOfPeriod);
}

export const generateReportingPeriodsByQuarters = (
  paymentPeriods: Array<PaymentPeriod>,
  selectedLicenses: Array<PaymentPeriod>,
  preSelectedPeriod: Period | undefined,
  shift: number = 0
): Array<PaymentPeriod> => {
  // Generate quarters for the current year and previous three years
  const currentYear = dayjs().year();
  const currentDate = dayjs();

  const quarters: Array<PaymentPeriod> = [];

  for (let year = currentYear; year > currentYear - 4; year--) {
    for (let quarter = 1; quarter <= 4; quarter++) {
      const { startDate: startDayjs, endDate: endDayjs } =
        getQuarterStartEndDates(year, quarter, shift);

      const startDate = startDayjs.format('YYYY-MM-DD');
      const endDate = endDayjs.format('YYYY-MM-DD');
      const endYear = endDayjs.year();

      // Check if the end date of the quarter is within the last 36 months
      const monthsDifference = currentDate.diff(endDate, 'months');

      if (
        endDayjs.isBefore(currentDate) &&
        monthsDifference >= 0 &&
        monthsDifference <= 36
      ) {
        // Find payment periods matching the start and end dates
        const matchingPeriod = matchPeriod(paymentPeriods, startDate, endDate);
        const matchSelPeriod = matchPeriod(
          selectedLicenses,
          startDate,
          endDate
        );

        const isSelectedOnLoad = calculateSelectedOnLoad(
          preSelectedPeriod,
          matchSelPeriod,
          startDate,
          endDate
        );

        // Add selected property to all licenses if matching period found, otherwise use empty array
        const licenses = addsLicensesSelectedProperty(
          matchingPeriod?.licenses ?? [],
          matchSelPeriod,
          selectedLicenses
        );
        const firstDateOfPeriod = getFirstDateOfQuarter(startDayjs);

        quarters.push({
          quarter,
          year: endYear,
          startDate,
          endDate,
          label: `${startDayjs.format('MMM YYYY')} - ${endDayjs.format('MMM YYYY')}`,
          selected: isSelectedOnLoad,
          licenses: licenses,
          id: firstDateOfPeriod.unix().toString(),
        });
      }
    }
  }

  //default select to the most recent period if nothing has been selected
  const selectedFound = quarters.some((quarter) => quarter.selected);

  // If no quarter is selected, find the most recent one
  if (!selectedFound) {
    const mostRecentQuarter = quarters.reduce((prev, current) => {
      const prevStartDate = dayjs(prev.startDate);
      const currentStartDate = dayjs(current.startDate);
      return prevStartDate.isAfter(currentStartDate) ? prev : current;
    });

    if (mostRecentQuarter) {
      mostRecentQuarter.selected = true;
    }
  }

  quarters.sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year; // Sort years in descending order
    }
    return (a.quarter as number) - (b.quarter as number); // Sort quarters in ascending order
  });

  return quarters;
};

export const generateReportingPeriodsByMonth = (
  paymentPeriods: Array<PaymentPeriod>,
  selectedLicenses: Array<PaymentPeriod>,
  preSelectedPeriod: Period | undefined
): Array<PaymentPeriod> => {
  const currentDate = dayjs();

  // Generate the list of the last 36 months, not including the current month
  const months = [];
  for (let i = 1; i < 36; i++) {
    const date = currentDate.subtract(i, 'month');

    const startDate = date.startOf('month').format('YYYY-MM-DD');
    const endDate = date.endOf('month').format('YYYY-MM-DD');

    // Find payment periods matching the start and end dates
    const matchingPeriod = matchPeriod(paymentPeriods, startDate, endDate);
    const matchSelPeriod = matchPeriod(selectedLicenses, startDate, endDate);

    const isSelectedOnLoad = calculateSelectedOnLoad(
      preSelectedPeriod,
      matchSelPeriod,
      startDate,
      endDate
    );

    const licenses = addsLicensesSelectedProperty(
      matchingPeriod?.licenses ?? [],
      matchSelPeriod,
      selectedLicenses
    );

    const firstDateOfPeriod = date.startOf('month');

    months.push({
      year: date.year(),
      startDate: startDate,
      endDate: endDate,
      label: date.format('MMMM YYYY'),
      selected: isSelectedOnLoad,
      licenses: licenses,
      id: firstDateOfPeriod.unix().toString(),
    });
  }
  // default select to the most recent period if nothing has been selected
  const selectedFound = months.some((amonths) => amonths.selected);

  // If no quarter is selected, find the most recent one
  if (!selectedFound) {
    const mostRecentMonth = months.reduce((prev, current) => {
      const prevStartDate = dayjs(prev.startDate);
      const currentStartDate = dayjs(current.startDate);
      return prevStartDate.isAfter(currentStartDate) ? prev : current;
    });

    if (mostRecentMonth) {
      mostRecentMonth.selected = true;
    }
  }

  months.sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year; // Sort years in descending order
    }
    return dayjs(a.startDate).diff(dayjs(b.startDate)); // Sort months in ascending order
  });

  return months;
};

/**
 *
 * @param paymentPeriods - the periods resulting in the call of the endpoint totPaymentPeriods
 * @param selectedLicenses -
 * @param periodType
 * @returns
 */
export const generateTaxablePeriods = (
  paymentPeriods: Array<PaymentPeriod>,
  selectedLicenses: Array<PaymentPeriod>,
  preSelectedPeriod: Period | undefined,
  periodType: TAX_PAYMENT_PERIOD_TYPES_ENUM
): Array<PaymentPeriod> => {
  if (periodType === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY) {
    return generateReportingPeriodsByQuarters(
      paymentPeriods,
      selectedLicenses,
      preSelectedPeriod
    );
  } else if (periodType === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL) {
    return generateReportingPeriodsByQuarters(
      paymentPeriods,
      selectedLicenses,
      preSelectedPeriod,
      -1
    );
  }

  return generateReportingPeriodsByMonth(
    paymentPeriods,
    selectedLicenses,
    preSelectedPeriod
  );
};
